// Features: M004-F0798 (WebUI #263, slice B3: the billing-profile history panel)
// Tests: T-M004-0430 (history renderer, BillingProfileHistory.test.js), T-M004-0431 (Playwright mock-contract, teacher ledger)
//
// Opened from the "History" link in the drawer summary. Reads customer_billing_profile.history (newest first), limit 20, and
// pages older revisions with before_rev = the lowest rev shown. Each changed path renders as "<label>: <before> → <after>",
// taken from the entry's whole before/after snapshots. No raw JSON is rendered.
import React, { useEffect, useState } from 'react';
import { customerBillingProfileApi } from '../../../services/customerBillingProfileApi';
import { errorCode, READ_CONDITIONS } from '../../../services/billingProfileAdapter';
import { READ_FAILED } from './billingProfileModel';

export const PAGE_LIMIT = 20;
export const EMPTY_SENTENCE = 'No changes have been saved yet.';
export const IMMEDIATELY_SENTENCE = 'Applies immediately';

const PATH_LABEL = Object.freeze({
  'credit.mode': 'Credit mode',
  'credit.display': 'Credit display',
  groups: 'Groups',
  apportionments: 'Bill-part rules',
});
const MODE_LABEL = { auto_on_invoice: 'Automatic on invoice', customer_controlled: 'Customer-controlled' };
const DISPLAY_LABEL = { on_invoice: 'Shown on the invoice', statement_only: 'Statement only' };
const ARRAY_NOUN = { groups: ['group', 'groups'], apportionments: ['rule', 'rules'] };

// The same read-failure mapping as the drawer: the coded conditions get their own sentence, anything else the generic one.
const readFailureSentence = (cause) => READ_CONDITIONS[errorCode(cause)] || READ_FAILED;

const londonDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'long', year: 'numeric' });
const londonDateTime = (iso) => new Date(iso).toLocaleString('en-GB', {
  timeZone: 'Europe/London', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const valueAt = (snapshot, path) => path.split('.').reduce((node, key) => (node && typeof node === 'object' ? node[key] : undefined), snapshot);

// A scalar in words; arrays as counts; anything else as "changed" (never JSON).
function scalarText(path, value) {
  if (path === 'credit.mode') return value ? (MODE_LABEL[value] || 'Changed') : 'Organisation default';
  if (path === 'credit.display') return value ? (DISPLAY_LABEL[value] || 'Changed') : 'Organisation default';
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return 'changed';
}

// The "<before> → <after>" text for one changed path, from the two whole snapshots.
export function pathChangeText(path, before, after) {
  const b = valueAt(before, path);
  const a = valueAt(after, path);
  if (ARRAY_NOUN[path] || Array.isArray(a) || Array.isArray(b)) {
    const [one, many] = ARRAY_NOUN[path] || ['item', 'items'];
    const count = (v) => (Array.isArray(v) ? `${v.length} ${v.length === 1 ? one : many}` : 'none');
    if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) return 'changed';
    return `${count(b)} → ${count(a)}`;
  }
  return `${scalarText(path, b)} → ${scalarText(path, a)}`;
}

export const pathLabel = (path) => PATH_LABEL[path] || path.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function HistoryEntry({ entry }) {
  const who = entry.by_name || entry.by_user_id;
  const immediate = !entry.effective_from || Date.parse(entry.effective_from) <= Date.parse(entry.at);
  return (
    <li className="bps-history-entry" data-testid="history-entry">
      <p className="bps-history-meta">
        <strong>Revision {entry.rev}</strong> · <span>{who}</span> · <time dateTime={entry.at}>{londonDateTime(entry.at)}</time>
      </p>
      <p>{entry.reason}</p>
      <p data-testid="history-effective">{immediate ? IMMEDIATELY_SENTENCE : `Applies from ${londonDate(entry.effective_from)}`}</p>
      {(entry.changed_paths || []).length ? (
        <ul className="bps-history-paths">
          {entry.changed_paths.map((path) => (
            <li key={path} data-testid="history-path">{pathLabel(path)}: {pathChangeText(path, entry.before, entry.after)}</li>
          ))}
        </ul>
      ) : null}
      {entry.superseded_by_rev != null ? (
        <p data-testid="history-superseded">Replaced by revision {entry.superseded_by_rev} before it took effect</p>
      ) : null}
    </li>
  );
}

export default function BillingProfileHistory({ orgId, userId, onBack }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [olderLoading, setOlderLoading] = useState(false);
  const [olderError, setOlderError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = (beforeRev) => customerBillingProfileApi.history(orgId, userId, {
    limit: PAGE_LIMIT, ...(beforeRev === undefined ? {} : { before_rev: beforeRev }),
  });

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    fetchPage().then((res) => {
      if (!current) return;
      const page = res?.data?.data?.entries || [];
      setEntries(page);
      setHasMore(page.length === PAGE_LIMIT);
      setLoading(false);
    }).catch((cause) => {
      if (!current) return;
      setError(readFailureSentence(cause));
      setLoading(false);
    });
    return () => { current = false; };
  }, [orgId, userId, reloadToken]);

  // Older revisions: before_rev is the lowest rev shown. The shown entries stay if the page fails.
  const loadOlder = async () => {
    const oldest = entries[entries.length - 1];
    if (!oldest || olderLoading) return;
    setOlderLoading(true);
    setOlderError('');
    try {
      const res = await fetchPage(oldest.rev);
      const page = res?.data?.data?.entries || [];
      setEntries((shown) => [...shown, ...page]);
      setHasMore(page.length === PAGE_LIMIT);
    } catch (cause) {
      setOlderError(readFailureSentence(cause));
    } finally {
      setOlderLoading(false);
    }
  };

  const renderList = () => {
    if (loading) return <p role="status" aria-busy="true">Loading history…</p>;
    if (error) {
      return (
        <div role="alert">
          <p>{error}</p>
          <button type="button" className="bps-secondary" onClick={() => setReloadToken((t) => t + 1)}>Retry</button>
        </div>
      );
    }
    if (!entries.length) return <p data-testid="history-empty">{EMPTY_SENTENCE}</p>;
    return (
      <>
        <ol className="bps-history-list">
          {entries.map((entry) => <HistoryEntry key={entry.rev} entry={entry} />)}
        </ol>
        {olderError ? <p role="alert" data-testid="history-older-error">{olderError}</p> : null}
        {hasMore ? (
          <button type="button" className="bps-secondary" data-testid="history-older" disabled={olderLoading} onClick={loadOlder}>
            {olderLoading ? 'Loading…' : 'Load older'}
          </button>
        ) : null}
      </>
    );
  };

  return (
    <section className="bps-section" aria-labelledby="bp-history-heading" data-testid="billing-history">
      <h3 id="bp-history-heading">History</h3>
      <button type="button" className="bps-secondary" onClick={onBack}>Back to billing profile</button>
      {renderList()}
    </section>
  );
}
